import React, { useRef, useState } from 'react';
import styles from './CustomSearchSelect.module.scss';
import SearchInput, { type ISearchInput } from '../SearchInput/SearchInput';
import { isNullOrUndefined } from '../../../helpers/object-helpers';
import ChevronDown from '../../Icons/ChevronDown';
import ChevronUp from '../../Icons/ChevronUp';
import { type IOption } from '../../shared/models/IOption';
import DropdownOptionsList from '../../shared/DropdownOptionsList/DropdownOptionsList';
import SelectedOption from '../SelectedOption/SelectedOption';

export interface ICustomSearchSelect {
  showTags?: boolean;
  options: IOption[];
  prioritizedOptions: IOption[];
  onChange?: (value: IOption | null) => void;
  customOptionsFilter?: (collection: IOption[], filter: string) => IOption[];
  id: string;
  placeholder?: string;
  disabled?: boolean;
  isValid?: boolean;
  defaultOption?: IOption;
  searchPlaceholder: string;
  searchAriaLabel: string;
  prioritizedItemsAlertText?: string;
  clearSearchButtonAriaLabel?: string;
  noOptionsFoundText?: string;
}

export default function CustomSearchSelect({
  showTags = false,
  options,
  prioritizedOptions,
  onChange,
  customOptionsFilter,
  id,
  placeholder,
  disabled = false,
  isValid,
  defaultOption,
  searchPlaceholder,
  searchAriaLabel,
  prioritizedItemsAlertText,
  clearSearchButtonAriaLabel,
  noOptionsFoundText = 'No results found. Try using another search!',
}: ICustomSearchSelect) {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const [selectedOption, setSelectedOption] = useState<IOption | null>(
    !isNullOrUndefined(defaultOption)
      ? defaultOption
      : !isNullOrUndefined(placeholder)
        ? null
        : options[0],
  );

  const [filter, setFilter] = useState('');

  function filterOptions(collection: IOption[]): IOption[] {
    if (typeof customOptionsFilter === 'function') return customOptionsFilter(collection, filter);

    return filter.length > 0
      ? collection.filter((option) => option.text.toLowerCase().includes(filter.toLowerCase()))
      : collection;
  }

  const filteredOptions = filterOptions(options);
  const filteredPrioritizedOptions = filterOptions(prioritizedOptions);
  const isAnyFilteredOptions = filteredOptions.length > 0 || filteredPrioritizedOptions.length > 0;

  const dropdownValidationClass = !isNullOrUndefined(isValid)
    ? isValid
      ? styles.valid
      : styles.invalid
    : '';

  const buttonRef = useRef<HTMLButtonElement>(null);

  const listRef = useRef<HTMLUListElement>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  function toggleDropdown() {
    if (dropdownOpen) setFilter('');
    setDropdownOpen(!dropdownOpen);
  }

  function onOptionClick(option: IOption) {
    setSelectedOption(option);
    toggleDropdown();
    if (onChange !== undefined) onChange(option);
  }

  function isOptionSelected() {
    return selectedOption !== null;
  }

  function unselect() {
    setSelectedOption(null);
    if (onChange !== undefined) onChange(null);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!dropdownOpen) {
        setDropdownOpen(true);  
      }
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setDropdownOpen(false);
    }
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    if(!event.currentTarget.contains(event.relatedTarget) && dropdownOpen) {
      setDropdownOpen(false);
    }
  }

  function handleSearchInputArrowDown(){
    (listRef.current?.childNodes[0] as HTMLElement).focus();
  }

  const initialValue = placeholder ?? selectedOption?.text;

  const searchInputProps: ISearchInput = {
    id: `${id}-search`,
    ariaLabel: searchAriaLabel,
    placeholder: searchPlaceholder,
    onChange: setFilter,
    value: filter,
    clearSearchButtonAriaLabel,
    onArrowDown: handleSearchInputArrowDown,
  };

  return (
    <div className={styles.selectContainer} onBlur={handleBlur}>
      <button
        ref={buttonRef}
        id={`${id}-button`}
        className={dropdownValidationClass}
        aria-haspopup="listbox"
        role="combobox"
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        aria-controls={`${id}-list`}
        aria-expanded={dropdownOpen}
        aria-labelledby={`${id}-label`}
      >
        <span
          className={
            disabled
              ? styles.disabledOptionText
              : !isOptionSelected() && placeholder != null
                ? styles.placeholderText
                : ''
          }
        >
          {selectedOption?.text ?? initialValue}
        </span>
        {dropdownOpen ? <ChevronUp /> : <ChevronDown />}
      </button>
      <div className={[styles.optionsContainer, !dropdownOpen ? styles.hideOptions : ''].join(' ')}>
        <div className={styles.searchInputContainer}>
          <SearchInput 
          {...searchInputProps} 
          inputRef={searchInputRef}
          />
          {showTags && !isNullOrUndefined(selectedOption) && (
            <div className={styles.selectedOptionsContainer}>
              <SelectedOption option={selectedOption} onUnselect={unselect} />
            </div>
          )}
        </div>
        {isAnyFilteredOptions && (
          <>
            {(prioritizedItemsAlertText?.length ?? 0) > 0 && (
              <div className={styles.hidden} role="alert">
                {prioritizedItemsAlertText}
              </div>
            )}
            
              <DropdownOptionsList
                id={id}
                options={filteredOptions}
                prioritizedOptions={filteredPrioritizedOptions}
                selectedOption={selectedOption}
                onOptionClick={onOptionClick}
                optionStyle={'TwoLine'}
                listRef={listRef}
                dropdownOpen={dropdownOpen}
              />
          </>
        )}
        {!isAnyFilteredOptions && (
          <div className={styles.noOptionsFound} role="alert">
            {noOptionsFoundText}
          </div>
        )}
      </div>
    </div>
  );
}
